import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { chemicalsAPI } from '../services/api';

function ChemicalDetail() {
  const { chemblId } = useParams();
  const navigate = useNavigate();
  const [chemical, setChemical] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChemicalDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await chemicalsAPI.getChEMBL(chemblId);
        if (response.data && response.data.molecule) {
          setChemical(response.data.molecule);
        } else {
          setError('Chemical not found');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChemicalDetail();
  }, [chemblId]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading chemical details...</div>
      </div>
    );
  }

  if (error || !chemical) {
    return (
      <div className="page-container">
        <div style={{
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '2px solid var(--light-bg)'
        }}>
          <button 
            onClick={() => navigate(-1)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1.25rem',
              backgroundColor: 'white',
              color: 'var(--text-color)',
              border: '2px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <span>←</span>
            <span>Back to Search</span>
          </button>
        </div>
        <div className="error">Error: {error || 'Chemical not found'}</div>
      </div>
    );
  }

  const compoundName = chemical.pref_name || 'Unknown Chemical';

  return (
    <div className="page-container">
      <div style={{
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '2px solid var(--light-bg)'
      }}>
        <button 
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.625rem 1.25rem',
            backgroundColor: 'white',
            color: 'var(--text-color)',
            border: '2px solid var(--border-color)',
            borderRadius: '8px',
            fontSize: '0.95rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          <span>←</span>
          <span>Back to Search</span>
        </button>
      </div>

      <div style={{ 
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        padding: '2rem',
        borderRadius: '16px',
        marginBottom: '2rem',
        boxShadow: '0 8px 32px rgba(17, 153, 142, 0.2)'
      }}>
        <h1 style={{
          color: 'white',
          fontSize: '2.25rem',
          fontWeight: '700',
          marginBottom: '1rem'
        }}>{compoundName}</h1>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{
            backgroundColor: 'rgba(255,255,255,0.25)',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}>ChEMBL ID: {chemical.molecule_chembl_id}</span>
          {chemical.max_phase && (
            <span style={{
              backgroundColor: 'rgba(255,255,255,0.9)',
              color: '#11998e',
              padding: '0.5rem 1rem',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}>Max Phase: {chemical.max_phase}</span>
          )}
        </div>
      </div>

      {chemical.molecule_properties && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid var(--light-bg)'
        }}>
          <h2 style={{
            color: 'var(--secondary-color)',
            fontSize: '1.5rem',
            marginBottom: '1.5rem',
            paddingBottom: '0.75rem',
            borderBottom: '3px solid var(--primary-color)',
            display: 'inline-block'
          }}>Molecular Properties</h2>
          <div className="info-grid">
            <div className="info-item">
              <strong>Molecular Formula:</strong>
              <span>{chemical.molecule_properties.full_molformula || 'N/A'}</span>
            </div>
            <div className="info-item">
              <strong>Molecular Weight:</strong>
              <span>{chemical.molecule_properties.full_mwt || 'N/A'}</span>
            </div>
            <div className="info-item">
              <strong>ALogP:</strong>
              <span>{chemical.molecule_properties.alogp || 'N/A'}</span>
            </div>
            <div className="info-item">
              <strong>HBA:</strong>
              <span>{chemical.molecule_properties.hba || 'N/A'}</span>
            </div>
            <div className="info-item">
              <strong>HBD:</strong>
              <span>{chemical.molecule_properties.hbd || 'N/A'}</span>
            </div>
            <div className="info-item">
              <strong>Aromatic Rings:</strong>
              <span>{chemical.molecule_properties.aromatic_rings || 'N/A'}</span>
            </div>
          </div>
        </div>
      )}

      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid var(--light-bg)'
      }}>
        <h2 style={{
          color: 'var(--secondary-color)',
          fontSize: '1.5rem',
          marginBottom: '1.5rem',
          paddingBottom: '0.75rem',
          borderBottom: '3px solid var(--primary-color)',
          display: 'inline-block'
        }}>Source Information</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#e8f4f8', 
            borderRadius: '8px',
            borderLeft: '4px solid #3498db'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: '#2c3e50' }}>
              🔗 ChEMBL Database
            </h3>
            <p style={{ margin: '0 0 0.75rem 0', color: '#7f8c8d', fontSize: '0.9em' }}>
              This information is sourced from ChEMBL, a manually curated database of bioactive molecules with drug-like properties.
            </p>
            <a 
              href={`https://www.ebi.ac.uk/chembl/compound_report_card/${chemical.molecule_chembl_id}/`}
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.875rem 1.75rem',
                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '10px',
                fontWeight: '600',
                fontSize: '1rem',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)'
              }}
            >
              <span>View on ChEMBL</span>
            </a>
          </div>
        </div>
      </div>

      <div style={{ 
        marginTop: '2rem', 
        display: 'flex', 
        gap: '1rem', 
        flexWrap: 'wrap',
        paddingTop: '2rem',
        borderTop: '2px solid var(--light-bg)'
      }}>
        <button onClick={() => navigate(-1)} style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: 'white',
          color: 'var(--primary-color)',
          border: '2px solid var(--primary-color)',
          borderRadius: '8px',
          fontWeight: '600',
          cursor: 'pointer'
        }}>
          ← Back to Search
        </button>
        <Link to="/chemicals" style={{
          padding: '0.75rem 1.5rem',
          backgroundColor: 'var(--primary-color)',
          color: 'white',
          border: '2px solid var(--primary-color)',
          borderRadius: '8px',
          fontWeight: '600',
          textDecoration: 'none'
        }}>
          🔍 New Search
        </Link>
      </div>
    </div>
  );
}

export default ChemicalDetail;
